import React, { useState, useEffect } from 'react';
import { useTranslation } from '../context/LanguageContext';
import { WifiOff, ShieldAlert } from 'lucide-react';
import { globalCircuitBreaker, CircuitState } from '../utils/circuitBreaker';

export const OfflineBanner: React.FC = () => {
  const { t } = useTranslation();
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [circuitState, setCircuitState] = useState<CircuitState>(globalCircuitBreaker.getState());
  const [lastOnlineTime, setLastOnlineTime] = useState<string>(() => {
    return localStorage.getItem('fhpf_last_sync') || new Date().toLocaleTimeString();
  });

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      const nowStr = new Date().toLocaleTimeString();
      setLastOnlineTime(nowStr);
      localStorage.setItem('fhpf_last_sync', nowStr);
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const unsubscribe = globalCircuitBreaker.onStateChange((state) => {
      setCircuitState(state);
    });

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      unsubscribe();
    };
  }, []);

  if (!isOnline) {
    return (
      <div className="bg-amber-500 text-navy-950 px-4 py-2 text-xs sm:text-sm font-medium flex items-center justify-between shadow-inner">
        <div className="max-w-5xl mx-auto w-full flex items-center gap-2">
          <WifiOff className="w-4 h-4 shrink-0" />
          <span>
            {t('offline.banner')} {lastOnlineTime}. {t('offline.queueNotice')}
          </span>
        </div>
      </div>
    );
  }

  if (circuitState === 'OPEN') {
    return (
      <div className="bg-sky-600 text-white px-4 py-2 text-xs sm:text-sm font-medium flex items-center justify-between shadow-inner">
        <div className="max-w-5xl mx-auto w-full flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 shrink-0 text-amber-300" />
          <span>
            cPanel Financial API is currently undergoing maintenance. Direct read-only ledger is active via Firebase.
          </span>
        </div>
      </div>
    );
  }

  return null;
};
